import React, { useEffect, useRef, useState } from 'react';

import './ButtonDropdown.scss';

const ROLES_IDS = ['remove', 'view', 'comment', 'edit', 'editadmin', 'own'];
const ROLES_MAP = {
    remove: 'Remove',
    view: 'Viewer',
    comment: 'Commenter',
    edit: 'Editor',
    editadmin: 'Editor + Admin',
    own: 'Owner',
}

function buildRoleName(role) {
    if (ROLES_IDS.includes(role)) {
        return ROLES_MAP[role];
    } else {
        return ROLES_MAP['view'];
    }
}

function ButtonDropdown({ memberId, memberRole = '', menuItems = [] }) {
    const [isOpen, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleItemClick = (callback, newMemberRole) => {
        if (typeof callback === 'function') {
            callback(memberId, newMemberRole);
        }
        setOpen(false);
    };

    return (
        <div ref={ref} className={`btn-dropdown_wrap `}>
            <button
                type="button"
                className={`btn-dropdown_btn ${isOpen ? 'selected' : ''} ${memberRole}`}
                onClick={() => setOpen(prev => !prev)}
            >
                <div className="left-part">{buildRoleName(memberRole)}</div>
            </button>

            {isOpen && (
                <ul className="btn-dropdown_menu">
                    {menuItems.map((item, index) => {
                        if (item.line) {
                            return <div key={index} className="horizontal-line"></div>;
                        } else {
                            return (
                                <li key={index} className="btn-dropdown_menu-item">
                                    <button
                                        type="button"
                                        className={`btn-dropdown_menu-item-btn ${item.role === memberRole ? 'selected' : ''}`}
                                        onClick={() => handleItemClick(item.onClick, item.role)}
                                    >
                                        {item.role === 'editadmin' ? <svg className="editadmin" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15.5H7.5C6.10444 15.5 5.40665 15.5 4.83886 15.6722C3.56045 16.06 2.56004 17.0605 2.17224 18.3389C2 18.9067 2 19.6044 2 21M19 21V15M16 18H22M14.5 7.5C14.5 9.98528 12.4853 12 10 12C7.51472 12 5.5 9.98528 5.5 7.5C5.5 5.01472 7.51472 3 10 3C12.4853 3 14.5 5.01472 14.5 7.5Z"></path></svg> : ''}
                                        {item.role === 'edit' ? <svg className="edit" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H6.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 6.28 2 7.12 2 8.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 22 5.12 22 6.8 22h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C20 19.72 20 18.88 20 17.2V13M8 16h1.675c.489 0 .733 0 .963-.055.204-.05.4-.13.579-.24.201-.123.374-.296.72-.642L21.5 5.5a2.121 2.121 0 0 0-3-3l-9.563 9.563c-.346.346-.519.519-.642.72a2 2 0 0 0-.24.579c-.055.23-.055.474-.055.963z"></path></svg> : ''}
                                        {item.role === 'comment' ? <svg className="comment" width="64px" height="64px" viewBox="0 -0.5 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><g> <path fillRule="evenodd" clipRule="evenodd" d="M5.5 12.9543C5.51239 14.0398 5.95555 15.076 6.73197 15.8348C7.50838 16.5936 8.55445 17.0128 9.64 17.0003H10.2L11.86 18.7323C12.0291 18.9036 12.2598 19 12.5005 19C12.7412 19 12.9719 18.9036 13.141 18.7323L14.8 17.0003H15.36C16.4456 17.0128 17.4916 16.5936 18.268 15.8348C19.0444 15.076 19.4876 14.0398 19.5 12.9543V8.04428C19.4731 5.7845 17.6198 3.97417 15.36 4.00028H9.64C7.38021 3.97417 5.5269 5.7845 5.5 8.04428V12.9543Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path> <path d="M9.5 8.25024C9.08579 8.25024 8.75 8.58603 8.75 9.00024C8.75 9.41446 9.08579 9.75024 9.5 9.75024V8.25024ZM15.5 9.75024C15.9142 9.75024 16.25 9.41446 16.25 9.00024C16.25 8.58603 15.9142 8.25024 15.5 8.25024V9.75024ZM9.5 11.2502C9.08579 11.2502 8.75 11.586 8.75 12.0002C8.75 12.4145 9.08579 12.7502 9.5 12.7502V11.2502ZM15.5 12.7502C15.9142 12.7502 16.25 12.4145 16.25 12.0002C16.25 11.586 15.9142 11.2502 15.5 11.2502V12.7502ZM9.5 9.75024H15.5V8.25024H9.5V9.75024ZM9.5 12.7502H15.5V11.2502H9.5V12.7502Z" fill="currentColor"></path> </g></svg> : ''}
                                        {item.role === 'view' ? <svg className="view" width="64px" height="64px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g> <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"></circle> <path d="M20.188 10.9343C20.5762 11.4056 20.7703 11.6412 20.7703 12C20.7703 12.3588 20.5762 12.5944 20.188 13.0657C18.7679 14.7899 15.6357 18 12 18C8.36427 18 5.23206 14.7899 3.81197 13.0657C3.42381 12.5944 3.22973 12.3588 3.22973 12C3.22973 11.6412 3.42381 11.4056 3.81197 10.9343C5.23206 9.21014 8.36427 6 12 6C15.6357 6 18.7679 9.21014 20.188 10.9343Z" stroke="currentColor" strokeWidth="1.5"></path> </g></svg> : ''}
                                        {item.role === 'remove' ? <svg className="remove" width="23px" height="23px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g> <path d="M16 6V5.2C16 4.0799 16 3.51984 15.782 3.09202C15.5903 2.71569 15.2843 2.40973 14.908 2.21799C14.4802 2 13.9201 2 12.8 2H11.2C10.0799 2 9.51984 2 9.09202 2.21799C8.71569 2.40973 8.40973 2.71569 8.21799 3.09202C8 3.51984 8 4.0799 8 5.2V6M10 11.5V16.5M14 11.5V16.5M3 6H21M19 6V17.2C19 18.8802 19 19.7202 18.673 20.362C18.3854 20.9265 17.9265 21.3854 17.362 21.673C16.7202 22 15.8802 22 14.2 22H9.8C8.11984 22 7.27976 22 6.63803 21.673C6.07354 21.3854 5.6146 20.9265 5.32698 20.362C5 19.7202 5 18.8802 5 17.2V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg> : ''}
                                        {buildRoleName(item.role)}
                                    </button>
                                </li>
                            )
                        }
                    })}
                </ul>
            )}
        </div>
    );
}

export default ButtonDropdown;







































// import { useEffect, useState } from 'react';

// import './Modal.scss';

// const Modal = ({
//     modalClass = '',
//     onShouldRender = null,
//     isOpen = false,
//     onCancel = null,
//     children,
//     isModalFullScreen = false,
// }) => {
//     const [shouldRender, setShouldRender] = useState(isOpen);
//     const [animationClass, setAnimationClass] = useState('');

//     useEffect(() => {
//         if (isOpen) {
//             onShouldRender?.();
//         }
//     }, [shouldRender]);

//     const handleOverlayClick = (e) => {
//         if (e.target === e.currentTarget) {
//             onCancel?.();
//         }
//     };

//     /* Close on ESC */
//     useEffect(() => {
//         if (!isOpen) return;

//         const handleKey = (e) => {
//             if (e.key === 'Escape' || e.key === 'Esc') {
//                 e.preventDefault();
//                 onCancel?.();
//             }
//         };
//         document.addEventListener('keydown', handleKey);
//         return () => document.removeEventListener('keydown', handleKey);
//     }, [isOpen, onCancel]);

//     if (!shouldRender) return null;

//     /* Modal markup (via portal so it sits above rest of DOM) */
//     return (
//         <div
//             className={`modal ${modalClass}`}>
//             <div className={`modal-overlay ${animationClass}`} onClick={handleOverlayClick}></div>
//             <div className={`modal-content ${animationClass} ${isModalFullScreen ? 'modal-fullscreen' : ''}`} onAnimationEnd={handleAnimationEnd}>
//                 {children}
//             </div>
//         </div>
//     );
// };

// export default Modal;
